import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { ApplicationConfigurationService } from '@app/core/services/ApplicationConfiguration.service';
import type { WorkflowClientCommandReference } from '@app/shared/workflow-commands/workflow-command-registry';
import type { WorkflowCompletionChecklist } from '@app/shared/workflow-commands/workflow-completion-requirements';

export interface HearingWorkflowLink {
  availability: 'available' | 'unavailable';
  reasonCode: string;
  workflowInstanceId: number | null;
  occurrenceId: number | null;
  route: string | null;
}

export interface HearingWorkflowActions {
  workflowInstanceId: number;
  occurrenceId: number | null;
  stepETag: string | null;
  blockReasons: { code: string; message: string }[];
  actions: {
    command: WorkflowClientCommandReference;
    label: string;
    enabled: boolean;
    blockReasons: { code: string; message: string }[];
  }[];
}

export interface HearingWorkflowTaskSnapshot {
  link: HearingWorkflowLink;
  requirements: WorkflowCompletionChecklist | null;
  availableActions: HearingWorkflowActions | null;
}

/** Read-only projection. Commands remain in the common workspace and recheck current authority. */
@Injectable()
export class HearingWorkflowTaskAuthorityService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ApplicationConfigurationService);
  private readonly options = {
    headers: new HttpHeaders({ 'skip-error-interceptor': 'yes', 'skip-loading-interceptor': 'yes' }),
    withCredentials: true,
  };
  readonly snapshot = signal<HearingWorkflowTaskSnapshot | null>(null);
  readonly loading = signal(false);
  readonly error = signal('');
  private generation = 0;

  /** Changing the selected proceeding withdraws the old witness before the next input is rendered. */
  clear(): void {
    this.generation++;
    this.snapshot.set(null);
    this.loading.set(false);
    this.error.set('');
  }

  async refresh(proceedingId: number): Promise<void> {
    const generation = ++this.generation;
    this.snapshot.set(null);
    this.error.set('');
    if (!Number.isSafeInteger(proceedingId) || proceedingId <= 0) {
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    try {
      const link = await firstValueFrom(this.http.get<HearingWorkflowLink>(
        this.config.apiUrl(`/api/v1/phms/hearing-records/${proceedingId}/workflow`), this.options));
      if (generation !== this.generation) return;
      if (link.availability === 'unavailable') {
        if (link.workflowInstanceId != null || link.occurrenceId != null || link.route != null)
          throw new Error('An unavailable workflow must not expose task context.');
        this.snapshot.set({ link, requirements: null, availableActions: null });
        return;
      }
      const workflowId = link.workflowInstanceId, occurrenceId = link.occurrenceId;
      if (link.availability !== 'available' || !Number.isSafeInteger(workflowId) || workflowId! <= 0
        || !Number.isSafeInteger(occurrenceId) || occurrenceId! <= 0
        || link.route !== `/workflows/${workflowId}/steps/${occurrenceId}`)
        throw new Error('The linked workflow task could not be confirmed.');
      const base = `/api/platform/workflows/${workflowId}`;
      const options = { ...this.options, params: { occurrenceId: occurrenceId! } };
      const { requirements, availableActions } = await firstValueFrom(forkJoin({
        requirements: this.http.get<WorkflowCompletionChecklist>(this.config.apiUrl(`${base}/requirements`), options),
        availableActions: this.http.get<HearingWorkflowActions>(this.config.apiUrl(`${base}/available-actions`), options),
      }));
      if (generation !== this.generation) return;
      if (requirements.occurrenceId !== occurrenceId || availableActions.occurrenceId !== occurrenceId
        || availableActions.workflowInstanceId !== workflowId || !availableActions.stepETag
        || typeof requirements.canComplete !== 'boolean' || !Array.isArray(requirements.requirements)
        || new Set(requirements.requirements.map(row => row.id)).size !== requirements.requirements.length
        || !Array.isArray(availableActions.actions) || !Array.isArray(availableActions.blockReasons)
        || availableActions.actions.some(action => typeof action.enabled !== 'boolean' || !action.command?.key
          || !Array.isArray(action.blockReasons)))
        throw new Error('The current workflow task changed.');
      this.snapshot.set({ link, requirements, availableActions });
    } catch (error) {
      if (generation !== this.generation) return;
      this.snapshot.set(null);
      this.error.set(error instanceof HttpErrorResponse && [401, 403, 404].includes(error.status)
        ? 'Current workflow access could not be confirmed. Refresh to check access again.'
        : 'Current workflow requirements and actions could not be confirmed. Refresh before continuing.');
    } finally {
      if (generation === this.generation) this.loading.set(false);
    }
  }

  ngOnDestroy(): void { this.clear(); }
}
